import type { CardMetadataItem } from '@workramp/ui';
import type { ContentTag } from '../types';
import { P, Tooltip } from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import TimeUtils from '~/common/time-utils';

const MAX_METADATA_ITEMS_IN_LIBRARY_CARD = 3;

export const formatTags = (tags: ContentTag[]) => tags.map((t) => t.name).join(', ');

export const addTagsMetadata = (
  metadata: CardMetadataItem[],
  tags: ContentTag[],
  color?: string,
): CardMetadataItem[] => {
  const TAGS_LABEL_PLURAL = I18n.t('js.learner.libraries.tags_label_plural', {
    count: tags.length,
  });
  const TAGS_LABEL_SINGULAR = I18n.t('js.learner.libraries.tags_label_singular');

  if (tags.length) {
    const metadataText = tags.length > 1 ? TAGS_LABEL_PLURAL : TAGS_LABEL_SINGULAR;

    const props: CardMetadataItem = {
      iconName: 'tag:regular',
      text: metadataText,
      tooltip: formatTags(tags),
    };

    if (color) {
      props.textColor = color;
      props.iconColor = color;
    }

    return [...metadata, props];
  }
  return metadata;
};

export const addDurationMetadata = (
  metadata: CardMetadataItem[],
  duration?: number | null,
  color?: string,
): CardMetadataItem[] => {
  if (duration) {
    const { timeEstimateDisplayNumber } = TimeUtils.getDisplayTimeEstimate(duration, true);

    const props: CardMetadataItem = {
      iconName: 'clock:regular',
      text: timeEstimateDisplayNumber,
    };

    if (color) {
      props.textColor = color;
      props.iconColor = color;
    }

    metadata.push(props);
  }
  return metadata;
};

// Builds metadata for a list of skills, showing a tooltip if there are more than 2 skills. Needs to support different densities and translation keys
// as LearnerHome and Library cards have different densities and translation keys for the skills metadata
export const buildSkillsMetadata = (
  skills: string[] | null,
  density: 'md' | 'lg',
  translationKey: string,
  color?: string,
) => {
  const numSkills = skills?.length;
  if (!numSkills) return [];

  const skillsList = skills.join(', ').trim();

  let skillsMetadataText = (
    <P size={0} density={density} maxLines={1} isExpandable>
      {skillsList}
    </P>
  );
  if (numSkills >= 3) {
    skillsMetadataText = (
      <Tooltip message={skillsList}>
        {I18n.t(translationKey, {
          count: numSkills,
        }).toLowerCase()}
      </Tooltip>
    );
  }

  const skillsMetadataProps: CardMetadataItem = {
    iconName: 'bulb:regular',
    text: skillsMetadataText,
  };

  if (color) {
    skillsMetadataProps.textColor = color;
    skillsMetadataProps.iconColor = color;
  }

  return [skillsMetadataProps];
};

export const addSkillsMetadata = (
  metadata: CardMetadataItem[],
  skills: string[],
  color?: string,
): CardMetadataItem[] => [...metadata, ...buildSkillsMetadata(skills, 'lg', 'js.learner.libraries.num_skills', color)];

export const buildCardMetadata = (duration: number | null, skills: string[], tags: ContentTag[], color?: string) => {
  let metadata: Array<CardMetadataItem> = [];

  metadata = addDurationMetadata(metadata, duration, color);
  metadata = addSkillsMetadata(metadata, skills, color);
  if (metadata.length < MAX_METADATA_ITEMS_IN_LIBRARY_CARD) {
    metadata = addTagsMetadata(metadata, tags, color);
  }

  return metadata;
};
